import React, { useMemo, useRef, useState } from "react";

import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";
import { formatString } from "@perceptron/utils/string";

import { Favicon } from "@demo/components/ToolCall";
import { rowEntrance } from "@demo/components/ToolCall/rowEntrance";
import { getLinkHost } from "@demo/libs/tool-calls/favicon";
import type { ToolCallLink } from "@demo/server/api/contracts/toolCall";
import { useToolCallStore, type ToolCallEntry } from "@demo/store/tool-calls";
import { strings } from "@demo/utils/strings";

/** How many site icons the collapsed row stacks. */
const STACKED_ICONS = 4;

/** How long the list takes to open (its grid-row transition), and so how long opening keeps it in view. */
const OPEN_MS = 300;

/**
 * While the list opens, scroll its pane a frame at a time so the whole list, down to the pane's bottom
 * padding, stays in view, moving with the list rather than after it. A list taller than the pane scrolls
 * the pill off the top; the list is what was asked for. The pane's own smooth scrolling cannot do this:
 * until the list has opened there is nothing further down to scroll to.
 */
function keepOpeningListInView(row: HTMLElement) {
  const pane = row.closest<HTMLElement>(".overflow-y-auto");
  if (!pane) return;
  const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const padding = parseFloat(getComputedStyle(pane).paddingBottom) || 0;
  const start = performance.now();
  const step = (now: number) => {
    const by = row.getBoundingClientRect().bottom + padding - pane.getBoundingClientRect().bottom;
    if (by > 0) pane.scrollTop += by;
    if (!reducedMotion && now - start < OPEN_MS + 50) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/** A URL as a source is identified: without its fragment or a trailing slash. */
const sourceKey = (url: string): string => url.replace(/#.*$/, "").replace(/\/+$/, "");

/**
 * The pages a run drew on, in the order they first appeared: every search result, and every page a visit
 * read (not the ones it could not). A page found by several calls is listed once.
 */
export function collectSources(calls: readonly ToolCallEntry[]): ToolCallLink[] {
  const seen = new Set<string>();
  const sources: ToolCallLink[] = [];
  for (const call of calls) {
    const links = [...call.links].sort((a, b) => a.index - b.index);
    for (const { link } of links) {
      if (!link.url) continue;
      if (call.family === "visit" && (link.status ?? "ok") !== "ok") continue;
      const key = sourceKey(link.url);
      if (seen.has(key)) continue;
      seen.add(key);
      sources.push(link);
    }
  }
  return sources;
}

/**
 * Under a finished answer that used search: the sites it drew on as a stack of icons and a count, which
 * opens into the full list of pages.
 */
export function SourcesRow() {
  const order = useToolCallStore((state) => state.order);
  const calls = useToolCallStore((state) => state.calls);
  const sources = useMemo(
    () => collectSources(order.map((id) => calls[id]).filter((call): call is ToolCallEntry => call !== undefined)),
    [order, calls],
  );
  const [isOpen, setIsOpen] = useState(false);
  // Mounted on first open, so the rows cascade in then, and kept so closing animates the full height.
  const [hasOpened, setHasOpened] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  if (sources.length === 0) return null;

  const copy = strings.agentOutput.sources;
  const count = sources.length === 1 ? copy.one : formatString(copy.many, sources.length);
  const stacked = sources.slice(0, STACKED_ICONS);

  return (
    <div className="flex min-w-0 flex-1 flex-col" data-testid="sources-row" ref={rowRef}>
      <button
        aria-expanded={isOpen}
        aria-label={`${count}. ${isOpen ? copy.hide : copy.show}`}
        className="group flex w-fit items-center gap-2 rounded-full border border-white/15 py-1 pl-1 pr-3 text-foreground-secondary transition-colors duration-200 ease-out label-2 hover:border-white/30 hover:text-foreground-primary"
        type="button"
        onClick={() => {
          setHasOpened(true);
          setIsOpen((open) => !open);
          if (!isOpen && rowRef.current) keepOpeningListInView(rowRef.current);
        }}
      >
        <span className="flex">
          {stacked.map((link, index) => {
            const host = getLinkHost(link);
            return (
              <span className={cn("rounded-[7px] ring-2 ring-black", index > 0 && "-ml-2")} key={link.url}>
                <Favicon alt="" className="size-5" host={host} key={host} url={link.url} />
              </span>
            );
          })}
        </span>
        <span>{count}</span>
        <Icon
          className={cn(
            "size-4 transition-transform duration-300 ease-out motion-reduce:transition-none",
            isOpen ? "rotate-90" : "rotate-0",
          )}
          name="chevron-right"
        />
      </button>

      {/* Opens in place, its height animated through the grid row, like the thinking and tool cards. */}
      <div
        aria-hidden={!isOpen || undefined}
        className={cn(
          "grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none",
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
        inert={!isOpen}
      >
        <div className="min-h-0 overflow-hidden">
          {hasOpened && (
            <ul aria-label={copy.listLabel} className="flex flex-col gap-2 pt-3" data-testid="sources-list">
              {sources.map((link, index) => {
                const host = getLinkHost(link);
                const entrance = rowEntrance(index, false);
                return (
                  <li
                    className={cn("flex min-w-px items-center gap-2", entrance.className)}
                    key={link.url}
                    style={entrance.style}
                  >
                    <Favicon alt="" host={host} key={host} url={link.url} />
                    <a
                      className="group flex min-w-px items-baseline gap-2"
                      href={link.url}
                      rel="noreferrer noopener"
                      target="_blank"
                      title={link.url}
                    >
                      <span className="truncate text-foreground-secondary transition-colors duration-200 ease-out label-2 group-hover:text-foreground-primary group-focus-visible:text-foreground-primary">
                        {link.title ?? host ?? link.url}
                      </span>
                      {link.title && host && <span className="shrink-0 text-foreground-disabled label-2">{host}</span>}
                    </a>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
